import {isNil, isNotNil} from 'ramda';

import {invalidateOnSuccess, omneticApi} from '@dms/api/base';

import {
  CancelWhatsAppSignupApiArg,
  CancelWhatsAppSignupApiArgSchema,
  CancelWhatsAppSignupApiResponse,
  CancelWhatsAppSignupApiResponseSchema,
  CheckWhatsAppConnectionApiArg,
  CheckWhatsAppConnectionApiArgSchema,
  CheckWhatsAppConnectionApiResponse,
  CheckWhatsAppConnectionApiResponseSchema,
  CompleteWhatsAppSignupApiArg,
  CompleteWhatsAppSignupApiArgSchema,
  CompleteWhatsAppSignupApiResponse,
  CompleteWhatsAppSignupApiResponseSchema,
  DisconnectWhatsAppApiArg,
  DisconnectWhatsAppApiArgSchema,
  DisconnectWhatsAppApiResponse,
  DisconnectWhatsAppApiResponseSchema,
  GetWhatsAppConnectionApiArg,
  GetWhatsAppConnectionApiArgSchema,
  GetWhatsAppConnectionApiResponse,
  GetWhatsAppConnectionApiResponseSchema,
  GetWhatsAppVerificationApiArg,
  GetWhatsAppVerificationApiArgSchema,
  GetWhatsAppVerificationApiResponse,
  GetWhatsAppVerificationApiResponseSchema,
  SendWhatsAppVerificationMessageApiArg,
  SendWhatsAppVerificationMessageApiArgSchema,
  SendWhatsAppVerificationMessageApiResponse,
  SendWhatsAppVerificationMessageApiResponseSchema,
  StartWhatsAppSignupApiArg,
  StartWhatsAppSignupApiArgSchema,
  StartWhatsAppSignupApiResponse,
  StartWhatsAppSignupApiResponseSchema,
  StartWhatsAppVerificationApiArg,
  StartWhatsAppVerificationApiArgSchema,
  StartWhatsAppVerificationApiResponse,
  StartWhatsAppVerificationApiResponseSchema,
} from './types';

const WHATSAPP_URL = '/dms/v1/tenant/integrated-service/whatsapp';

// Both queries carry the 'WhatsAppConfig' tag under their own id. Invalidating the bare tag
// (check, disconnect, signup, the manual form) refreshes both; an id refreshes only that query.
const CONNECTION_TAG = {type: 'WhatsAppConfig', id: 'connection'} as const;
const getVerificationTag = (verificationId: string) =>
  ({type: 'WhatsAppConfig', id: verificationId}) as const;

export const whatsAppConnectionApi = omneticApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (build) => ({
    getWhatsAppConnection: build.query<
      GetWhatsAppConnectionApiResponse,
      GetWhatsAppConnectionApiArg
    >({
      query: () => ({
        url: `${WHATSAPP_URL}/connection`,
      }),
      providesTags: [CONNECTION_TAG],
      extraOptions: {
        requestSchema: GetWhatsAppConnectionApiArgSchema,
        responseSchema: GetWhatsAppConnectionApiResponseSchema,
      },
    }),
    startWhatsAppSignup: build.mutation<StartWhatsAppSignupApiResponse, StartWhatsAppSignupApiArg>({
      query: (queryArg) => ({
        url: `${WHATSAPP_URL}/signup`,
        method: 'POST',
        body: queryArg,
        queryArg,
      }),
      extraOptions: {
        requestSchema: StartWhatsAppSignupApiArgSchema,
        responseSchema: StartWhatsAppSignupApiResponseSchema,
      },
    }),
    // A completed signup stores new credentials, so the legacy config and the connection state
    // both change. A failed one changes neither.
    completeWhatsAppSignup: build.mutation<
      CompleteWhatsAppSignupApiResponse,
      CompleteWhatsAppSignupApiArg
    >({
      query: (queryArg) => {
        const {signupId, ...body} = queryArg;
        return {
          url: `${WHATSAPP_URL}/signup/${signupId}/complete`,
          method: 'POST',
          body,
          queryArg,
        };
      },
      invalidatesTags: invalidateOnSuccess(
        (result: CompleteWhatsAppSignupApiResponse | undefined) =>
          result?.state === 'completed' ? ['WhatsAppConfig'] : []
      ),
      extraOptions: {
        requestSchema: CompleteWhatsAppSignupApiArgSchema,
        responseSchema: CompleteWhatsAppSignupApiResponseSchema,
      },
    }),
    cancelWhatsAppSignup: build.mutation<
      CancelWhatsAppSignupApiResponse,
      CancelWhatsAppSignupApiArg
    >({
      query: (queryArg) => {
        const {signupId, ...body} = queryArg;
        return {
          url: `${WHATSAPP_URL}/signup/${signupId}/cancel`,
          method: 'POST',
          body,
          queryArg,
        };
      },
      extraOptions: {
        requestSchema: CancelWhatsAppSignupApiArgSchema,
        responseSchema: CancelWhatsAppSignupApiResponseSchema,
      },
    }),
    // The check records any problem it finds on the connection, so the connection is refetched.
    checkWhatsAppConnection: build.mutation<
      CheckWhatsAppConnectionApiResponse,
      CheckWhatsAppConnectionApiArg
    >({
      query: () => ({
        url: `${WHATSAPP_URL}/connection/check`,
        method: 'POST',
      }),
      invalidatesTags: invalidateOnSuccess(['WhatsAppConfig']),
      extraOptions: {
        requestSchema: CheckWhatsAppConnectionApiArgSchema,
        responseSchema: CheckWhatsAppConnectionApiResponseSchema,
      },
    }),
    disconnectWhatsApp: build.mutation<DisconnectWhatsAppApiResponse, DisconnectWhatsAppApiArg>({
      query: () => ({
        url: `${WHATSAPP_URL}/connection`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidateOnSuccess(['WhatsAppConfig']),
      extraOptions: {
        requestSchema: DisconnectWhatsAppApiArgSchema,
        responseSchema: DisconnectWhatsAppApiResponseSchema,
      },
    }),
    startWhatsAppVerification: build.mutation<
      StartWhatsAppVerificationApiResponse,
      StartWhatsAppVerificationApiArg
    >({
      query: () => ({
        url: `${WHATSAPP_URL}/verification`,
        method: 'POST',
      }),
      extraOptions: {
        requestSchema: StartWhatsAppVerificationApiArgSchema,
        responseSchema: StartWhatsAppVerificationApiResponseSchema,
      },
    }),
    getWhatsAppVerification: build.query<
      GetWhatsAppVerificationApiResponse,
      GetWhatsAppVerificationApiArg
    >({
      query: ({verificationId}) => ({
        url: `${WHATSAPP_URL}/verification/${verificationId}`,
      }),
      providesTags: (_result, _error, {verificationId}) => [getVerificationTag(verificationId)],
      // The verification moving on (code received, reply delivered) changes the connection's
      // sending and receiving results. Only the connection is refreshed, so nothing ping-pongs.
      onQueryStarted: async (_arg, {dispatch, getCacheEntry, queryFulfilled}) => {
        const previousState = getCacheEntry().data?.state;
        const result = await queryFulfilled.catch(() => undefined);
        if (isNotNil(previousState) && isNotNil(result) && result.data.state !== previousState) {
          dispatch(omneticApi.util.invalidateTags([CONNECTION_TAG]));
        }
      },
      extraOptions: {
        requestSchema: GetWhatsAppVerificationApiArgSchema,
        responseSchema: GetWhatsAppVerificationApiResponseSchema,
      },
    }),
    // Sending can reveal an invalid token or revoked permission, which the connection then reports.
    // A rejected send means the verification moved on (expired, superseded), so it is refetched.
    sendWhatsAppVerificationMessage: build.mutation<
      SendWhatsAppVerificationMessageApiResponse,
      SendWhatsAppVerificationMessageApiArg
    >({
      query: (queryArg) => {
        const {verificationId, ...body} = queryArg;
        return {
          url: `${WHATSAPP_URL}/verification/${verificationId}/send-message`,
          method: 'POST',
          body,
          queryArg,
        };
      },
      invalidatesTags: (_result, error, {verificationId}) =>
        isNil(error) ? [CONNECTION_TAG] : [getVerificationTag(verificationId)],
      // The response is the updated verification, so it replaces the cached one without a refetch.
      onQueryStarted: async ({verificationId}, {dispatch, queryFulfilled}) => {
        const result = await queryFulfilled.catch(() => undefined);
        if (isNotNil(result)) {
          dispatch(
            whatsAppConnectionApi.util.updateQueryData(
              'getWhatsAppVerification',
              {verificationId},
              () => result.data
            )
          );
        }
      },
      extraOptions: {
        requestSchema: SendWhatsAppVerificationMessageApiArgSchema,
        responseSchema: SendWhatsAppVerificationMessageApiResponseSchema,
      },
    }),
  }),
});

export const {
  useGetWhatsAppConnectionQuery,
  useStartWhatsAppSignupMutation,
  useCompleteWhatsAppSignupMutation,
  useCancelWhatsAppSignupMutation,
  useCheckWhatsAppConnectionMutation,
  useDisconnectWhatsAppMutation,
  useStartWhatsAppVerificationMutation,
  useGetWhatsAppVerificationQuery,
  useSendWhatsAppVerificationMessageMutation,
} = whatsAppConnectionApi;
